
import { defineStore } from 'pinia'
import Echo from 'laravel-echo'
import axios from 'axios'

import { stopTimer } from '@/composables/useTimeLogout'
import { authChannel } from '@/utils/authChannel'

const API = '/api/v1/auth'

// Huella compartida entre pestañas, nunca el Bearer ni datos del usuario.
async function sessionFingerprint(token) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function safelyCleanup(cleanup) {
  try {
    cleanup()
  } catch {
    console.warn('No se pudo completar una limpieza auxiliar de sesión.')
  }
}

function getErrorMessage(error, fallback) {
  if (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT'
  ) {
    return 'La solicitud tardó demasiado. Intenta nuevamente.'
  }

  const data = error.response?.data

  if (typeof data?.message === 'string') {
    return data.message
  }

  if (data?.errors) {
    if (Array.isArray(data.errors)) {
      return data.errors.join(' ')
    }

    if (typeof data.errors === 'object') {
      return Object.values(data.errors)
        .flat()
        .join(' ')
    }
  }

  return fallback
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authUser: null,
    authToken: null,
    // Identidad lógica compartida: estable al renovar, distinta en cada login.
    authSessionId: null,
    device: null,
    device_token:null,
    tokenExpirationDate: null,
    permissions: [],
    mustChangePasswordModal: false,
    expires_in: 15,
    loading: false,
    errors: null,
    forgotPasswordMessage: null,
    resetPasswordMessage: null,

    // No persistir: identifica respuestas pertenecientes a una sesión anterior.
    sessionVersion: 0,
    isLoggingOut: false,

    // Estado no persistente de Passkeys
    passkeyLoading: false,
  }),

  getters: {
    user: (state) => state.authUser,
    token: (state) => state.authToken,
    permisos: (state) => state.permissions,

    isAuthenticated: (state) =>
      Boolean(state.authUser && state.authToken),

    can: (state) => (permission) =>
      state.permissions.includes(permission),

    passkeySupported: () =>
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof window.PublicKeyCredential !== 'undefined',
  },

  actions: {
    clearMessages() {
      this.errors = null
      this.forgotPasswordMessage = null
      this.resetPasswordMessage = null
    },

    /*
    |--------------------------------------------------------------------------
    | COMPLETAR AUTENTICACIÓN
    |--------------------------------------------------------------------------
    | Compartido por contraseña y Passkey.
    | Laravel debe devolver una respuesta equivalente
    | para ambos métodos de autenticación.
    */
    completeLogin(data, expectedVersion = this.sessionVersion) {
      if (expectedVersion !== this.sessionVersion) return false

      if (!data?.token || !data?.user) {
        throw new Error('Respuesta de autenticación incompleta.')
      }

      // Desconectar una conexión anterior
      this.disconnectEcho()

      this.sessionVersion += 1
      this.isLoggingOut = false
      this._logoutPromise = null
      this.loading = false
      this.passkeyLoading = false
      this._loginOperation = null
      this._passkeyOperation = null

      this.authToken = data.token
      this.authSessionId = crypto.randomUUID()
      this.authUser = data.user
      this.device = data.device ?? null
      this.expires_in = Number(data.expires_in ?? 15)

      this.tokenExpirationDate = data.expires_at
        ? new Date(data.expires_at)
        : new Date(Date.now() + this.expires_in * 60 * 1000)

      this.permissions = Array.isArray(data.permissions)
        ? [...data.permissions]
        : []

      // Nunca conservar el valor de un usuario anterior
      this.mustChangePasswordModal =
        Boolean(data.mustChangePassword)

      this.errors = null

      this.initializeEcho()

      this.router.replace({
        name: data.homeView || 'dashboard',
      })

      return true
    },

    /*
    |--------------------------------------------------------------------------
    | LOGIN TRADICIONAL
    |--------------------------------------------------------------------------
    */
    async login(form) {
      if (this.loading || this.passkeyLoading) return false

      this.loading = true
      this.errors = null
      const sessionVersion = this.sessionVersion
      const operation = Symbol('login')
      this._loginOperation = operation

      try {
        const deviceToken = localStorage.getItem('device_token')

        const headers = {}

        if (deviceToken) {
          headers['X-Device-Token'] = deviceToken
        }

        const { data } = await axios.post(
          `${API}/login`,
          {
            email: form.email,
            password: form.password,
          },
          {
            headers,
            timeout: 15000,
          }
        )

        return this.completeLogin(data, sessionVersion)

      } catch (error) {
        if (sessionVersion !== this.sessionVersion || this._loginOperation !== operation) return false
        this.errors = getErrorMessage(
          error,
          'No fue posible iniciar sesión.'
        )

        return false

      } finally {
        if (this._loginOperation === operation) {
          this.loading = false
          this._loginOperation = null
        }
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LOGIN CON PASSKEY
    |--------------------------------------------------------------------------
    | Requiere endpoints WebAuthn en Laravel:
    |
    | POST /passkeys/authenticate/options
    | POST /passkeys/authenticate/verify
    |
    | El backend debe asociar cada desafío a una sesión
    | temporal anónima y verificarlo una sola vez.
    */

    async loginWithPasskey() {
      if (this.loading || this.passkeyLoading) return false

      this.errors = null
      const sessionVersion = this.sessionVersion

      if (!this.passkeySupported) {
        this.errors =
          'Este navegador o conexión no permite usar Passkeys.'
        return false
      }

      this.passkeyLoading = true
      const operation = Symbol('passkey')
      this._passkeyOperation = operation

      try {
        const { startAuthentication } = await import(
          '@simplewebauthn/browser'
        )

        if (sessionVersion !== this.sessionVersion) return false

        // 1. Solicitar desafío al backend
        const { data: challenge } = await axios.post(
          `${API}/passkeys/authenticate/options`,
          {},
          {
            timeout: 15000
          }
        )

        if (sessionVersion !== this.sessionVersion) return false

        // 2. Solicitar autenticación biométrica o PIN
        const credential = await startAuthentication({
          optionsJSON: challenge.options
        })

        if (sessionVersion !== this.sessionVersion) return false

        // 3. Verificar credencial en Laravel
        const deviceToken = localStorage.getItem('device_token')

        const headers = {}

        if (deviceToken) {
          headers['X-Device-Token'] = deviceToken
        }

        const { data } = await axios.post(
          `${API}/passkeys/authenticate/verify`,
          {
            challenge_id: challenge.challenge_id,
            credential
          },
          {
            headers,
            timeout: 15000
          }
        )

        // 4. Crear sesión habitual de Medical Dhara
        return this.completeLogin(data, sessionVersion)

      } catch (error) {
        if (sessionVersion !== this.sessionVersion || this._passkeyOperation !== operation) return false
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'AbortError'
        ) {
          this.errors =
            'La autenticación fue cancelada o no se completó.'
        } else {
          this.errors = getErrorMessage(
            error,
            'No fue posible iniciar sesión con Passkey.'
          )
        }

        return false

      } finally {
        if (this._passkeyOperation === operation) {
          this.passkeyLoading = false
          this._passkeyOperation = null
        }
      }
    },

    /*
    |--------------------------------------------------------------------------
    | REGISTRAR UNA NUEVA PASSKEY
    |--------------------------------------------------------------------------
    | Solo para usuarios autenticados.
    | El backend debe exigir reautenticación reciente
    | antes de permitir agregar credenciales.
    */

    async registerPasskey(name = 'Mi dispositivo') {
      if (!this.authToken || !this.authUser) {
        this.errors = 'Debes iniciar sesión.'
        return false
      }

      if (this.passkeyLoading) return false

      if (!this.passkeySupported) {
        this.errors = 'Este dispositivo no admite Passkeys.'
        return false
      }

      this.passkeyLoading = true
      this.errors = null
      const sessionVersion = this.sessionVersion
      const token = this.authToken
      const operation = Symbol('passkey')
      this._passkeyOperation = operation

      try {
        const { startRegistration } = await import(
          '@simplewebauthn/browser'
        )

        if (sessionVersion !== this.sessionVersion) return false

        const config = {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 15000
        }

        // 1. Solicitar opciones de registro
        const { data: challenge } = await axios.post(
          `${API}/passkeys/register/options`,
          {},
          config
        )

        if (sessionVersion !== this.sessionVersion) return false

        // 2. Registrar credencial en el dispositivo
        const credential = await startRegistration({
          optionsJSON: challenge.options
        })

        if (sessionVersion !== this.sessionVersion) return false

        // 3. Verificar y almacenar Passkey en Laravel
        const { data } = await axios.post(
          `${API}/passkeys/register/verify`,
          {
            challenge_id: challenge.challenge_id,
            credential,
            name
          },
          config
        )

        return sessionVersion === this.sessionVersion ? data : false

      } catch (error) {
        if (sessionVersion !== this.sessionVersion || this._passkeyOperation !== operation) return false
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'AbortError'
        ) {
          this.errors =
            'El registro de Passkey fue cancelado.'
        } else {
          this.errors = getErrorMessage(
            error,
            'No fue posible registrar la Passkey.'
          )
        }

        return false

      } finally {
        if (this._passkeyOperation === operation) {
          this.passkeyLoading = false
          this._passkeyOperation = null
        }
      }
    },


    /*
    |--------------------------------------------------------------------------
    | LISTAR PASSKEYS DEL USUARIO
    |--------------------------------------------------------------------------
    */
    async getPasskeys() {
      if (!this.authToken) return []
      const sessionVersion = this.sessionVersion

      try {
        const { data } = await axios.get(
          `${API}/passkeys`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
          }
        )

        if (sessionVersion !== this.sessionVersion) return []
        return Array.isArray(data)
          ? data
          : data.passkeys ?? []

      } catch (error) {
        if (sessionVersion !== this.sessionVersion) return []
        this.errors = getErrorMessage(
          error,
          'No fue posible consultar las Passkeys.'
        )

        return []
      }
    },

    /*
    |--------------------------------------------------------------------------
    | REVOCAR PASSKEY
    |--------------------------------------------------------------------------
    | Laravel debe verificar propiedad de la credencial
    | y exigir reautenticación reciente.
    */
    async deletePasskey(id) {
      if (!this.authToken) return false
      const sessionVersion = this.sessionVersion

      try {
        await axios.delete(
          `${API}/passkeys/${encodeURIComponent(id)}`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
          }
        )

        return sessionVersion === this.sessionVersion

      } catch (error) {
        if (sessionVersion !== this.sessionVersion) return false
        this.errors = getErrorMessage(
          error,
          'No fue posible eliminar la Passkey.'
        )

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RECUPERAR CONTRASEÑA
    |--------------------------------------------------------------------------
    */
    async forgotPassword(form) {
      this.loading = true
      this.clearMessages()

      try {
        const { data } = await axios.post(
          `${API}/forgot-password`,
          {
            identifier: form.identifier,
          }
        )

        this.forgotPasswordMessage =
          data.message ||
          'Si existe una cuenta asociada, recibirás un correo.'

        return true

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No fue posible procesar la solicitud.'
        )

        return false

      } finally {
        this.loading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RESTABLECER CONTRASEÑA
    |--------------------------------------------------------------------------
    */
    async resetpassword(form) {
      this.loading = true
      this.clearMessages()

      try {
        const { data } = await axios.post(
          `${API}/reset-password`,
          {
            token: form.token,
            email: form.email,
            password: form.password,
            password_confirmation: form.password_confirmation,
          }
        )

        this.resetPasswordMessage =
          data.message || 'Contraseña restablecida correctamente.'

        return true

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No pudimos restablecer la contraseña.'
        )

        return false

      } finally {
        this.loading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LIMPIAR ESTADO DE AUTENTICACIÓN
    |--------------------------------------------------------------------------
    */
    clearAuth() {
      this.sessionVersion += 1

      this.authToken = null
      this.authSessionId = null
      this.authUser = null
      this.device = null
      this.tokenExpirationDate = null
      this.permissions = []
      this.mustChangePasswordModal = false
      this.expires_in = 15
      this.loading = false
      this.passkeyLoading = false
      this._loginOperation = null
      this._passkeyOperation = null

      this.clearMessages()

      // Compatibilidad con el almacenamiento anterior
      safelyCleanup(() => localStorage.removeItem('token'))

      safelyCleanup(() => this.stopAutoRefresh())
      safelyCleanup(() => this.disconnectEcho())
      safelyCleanup(() => stopTimer())
    },

    /*
    |--------------------------------------------------------------------------
    | LOGOUT
    |--------------------------------------------------------------------------
    */
    logout({ remote = true, broadcast = true } = {}) {
      if (this._logoutPromise) return this._logoutPromise

      const token = this.authToken
      const authSessionId = this.authSessionId

      this.isLoggingOut = true
      this.clearAuth()
      const sessionVersion = this.sessionVersion

      // El token capturado permite revocar sin conservar acceso local mientras espera la red.
      const completion = Promise.resolve().then(async () => {
        try {
          if (remote && token) {
            await axios.get(`${API}/logout`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 15000,
            })
            return { revoked: true }
          }
          return { revoked: null }
        } catch {
          // No imprimir el error Axios: puede contener el encabezado Bearer.
          console.warn('Sesión local cerrada; no se pudo confirmar la revocación remota.')
          return { revoked: false }
        } finally {
          if (this.sessionVersion === sessionVersion) this.isLoggingOut = false
          if (this._logoutPromise === completion) this._logoutPromise = null
        }
      })
      this._logoutPromise = completion

      if (broadcast && token) {
        Promise.resolve(authSessionId || sessionFingerprint(token)).then((sessionId) => {
          authChannel.postMessage({ type: 'logout', sessionId })
        }).catch(() => {
          console.warn('No fue posible notificar el cierre a otras pestañas.')
        })
      }

      // La navegación tampoco depende del canal ni de la respuesta de revocación.
      try {
        Promise.resolve(this.router.replace('/login')).catch(() => {})
      } catch {
        console.warn('Sesión local cerrada; no se pudo completar la navegación al login.')
      }
      return completion
    },

    async receiveLogout(message) {
      if (!this.authToken || typeof message?.sessionId !== 'string') return false
      const token = this.authToken
      const sessionVersion = this.sessionVersion
      try {
        const sessionId = this.authSessionId || await sessionFingerprint(token)
        if (sessionVersion !== this.sessionVersion || token !== this.authToken || sessionId !== message.sessionId) return false
        await this.logout({ remote: false, broadcast: false })
        return true
      } catch {
        console.warn('No fue posible comprobar el cierre recibido de otra pestaña.')
        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | VERIFICAR SESIÓN
    |--------------------------------------------------------------------------
    */
    async checkAuth() {
      if (this.isLoggingOut || !this.authToken) {
        return false
      }

      const sessionVersion = this.sessionVersion

      try {
        const { data } = await axios.get(
          `${API}/profile`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
            timeout: 15000,
          }
        )

        if (sessionVersion !== this.sessionVersion || this.isLoggingOut) return false

        // No reemplazamos authUser porque desconocemos
        // la estructura exacta de la respuesta /profile.
        return data

      } catch (error) {
        if (sessionVersion === this.sessionVersion && error.response?.status === 401) {
          await this.logout({ remote: false })
        }

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RENOVAR TOKEN
    |--------------------------------------------------------------------------
    | Conserva la ruta existente.
    | El backend debe validar el Bearer Token y obtener
    | la identidad desde la autenticación, no desde user_id.
    */
    async refreshToken() {
      if (this.isLoggingOut || !this.authToken) return false
      const sessionVersion = this.sessionVersion
      const token = this.authToken

      try {
        const { data } = await axios.post(
          `${API}/refreh-token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
            timeout: 15000,
          }
        )

        if (sessionVersion !== this.sessionVersion || this.isLoggingOut) return false

        if (!data.token) {
          throw new Error('No se recibió un token válido.')
        }

        // Compatibilidad con sesiones persistidas antes de introducir authSessionId.
        const sessionId = this.authSessionId || await sessionFingerprint(token)
        if (sessionVersion !== this.sessionVersion || this.isLoggingOut) return false
        this.authSessionId = sessionId
        this.authToken = data.token

        if (data.expires_in) {
          this.expires_in = Number(data.expires_in)
        }

        this.tokenExpirationDate = data.expires_at
          ? new Date(data.expires_at)
          : new Date(
            Date.now() + this.expires_in * 60 * 1000
          )

        // Actualizar credenciales de Echo
        this.initializeEcho()

        return true

      } catch (error) {
        if (sessionVersion !== this.sessionVersion) return false
        if (error.response?.status === 401) {
          await this.logout({ remote: false })
        } else {
          console.error('Error renovando token:', error)
        }

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RENOVACIÓN AUTOMÁTICA
    |--------------------------------------------------------------------------
    | Solo un intervalo activo por instancia.
    */
    startAutoRefresh(timeExpire = 1) {
      this.stopAutoRefresh()

      const minutes = Math.max(1, Number(timeExpire) || 1)

      this._refreshInterval = setInterval(async () => {
        if (!this.tokenExpirationDate || !this.authToken) {
          return
        }

        if (this._refreshInProgress) return

        const expiration = new Date(
          this.tokenExpirationDate
        ).getTime()

        const remaining = expiration - Date.now()
        const threshold = 5 * 60 * 1000

        if (remaining > 0 && remaining <= threshold) {
          this._refreshInProgress = true

          try {
            await this.refreshToken()
          } finally {
            this._refreshInProgress = false
          }
        }

        if (remaining <= 0) {
          await this.logout()
        }
      }, minutes * 60 * 1000)
    },

    stopAutoRefresh() {
      if (this._refreshInterval) {
        clearInterval(this._refreshInterval)
        this._refreshInterval = null
      }
    },

    /*
    |--------------------------------------------------------------------------
    | DESCONECTAR LARAVEL ECHO
    |--------------------------------------------------------------------------
    */
    disconnectEcho() {
      const echo = this.echo
      this.echo = null
      if (typeof window !== 'undefined') window.Echo = null
      safelyCleanup(() => echo?.disconnect())
    },

    /*
    |--------------------------------------------------------------------------
    | INICIALIZAR LARAVEL ECHO
    |--------------------------------------------------------------------------
    */
    initializeEcho() {
      if (!this.authToken) return

      this.disconnectEcho()

      try {
        this.echo = new Echo({
          broadcaster: 'pusher',
          key: import.meta.env.VITE_PUSHER_APP_KEY,
          cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,

          authEndpoint:
            `${import.meta.env.VITE_API_URL}/api/broadcasting/auth`,

          encrypted: false,
          wsPort: 80,
          wssPort: 443,
          wsHost: import.meta.env.VITE_PUSHER_HOST,

          enabledTransports: ['ws', 'wss'],

          auth: {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
              Accept: 'application/json',
            },
          },
        })

        window.Echo = this.echo

      } catch (error) {
        console.error(
          'Error inicializando Laravel Echo:',
          error
        )
      }
    },
  },

  /*
  |--------------------------------------------------------------------------
  | PERSISTENCIA PINIA
  |--------------------------------------------------------------------------
  | Conservamos temporalmente la estructura actual.
  | Para producción clínica conviene migrar el Bearer
  | Token a un mecanismo de almacenamiento más seguro.
  */
  persist: {
    pick: [
      'authUser',
      'authToken',
      'authSessionId',
      'device',
      // 'device_token',
      'tokenExpirationDate',
      'permissions',
      'expires_in',
      'mustChangePasswordModal',
    ],
  },
})
